import {
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  RoomContext,
} from "@livekit/components-react";
import { Room, Track, RoomEvent } from "livekit-client";
import "@livekit/components-styles";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getLivekitTokenApi,
  getMeetingById,
} from "@/modules/meetings/api/api-meeting";
import { toast } from "sonner";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { MeetingParticipant } from "../meetings/types/meeting.type";
import { BottomControlBar } from "./components/BottomControlBar";
import { ParticipantsGrid } from "./components/ParticipantsGrid";
import { MeetingChatPanel } from "./components/MeetingChatPanel";
import { ControlPanel } from "./components/ControlPanel";

export default function LiveKit() {
  const { user } = useAuth();
  const { meetingId, meetingCode } = useParams<{
    meetingId: string;
    meetingCode: string;
  }>();
  const navigate = useNavigate();

  const [room] = useState(
    () =>
      new Room({
        // Tối ưu chất lượng video cho màn hình của mỗi participant
        adaptiveStream: true,
        // Bật tối ưu tự động chất lượng audio/video
        dynacast: true,
      })
  );

  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isControlPanelOpen, setIsControlPanelOpen] = useState(false);

  // Lấy token và kết nối vào LiveKit room
  const fetchTokenAndConnect = useCallback(async () => {
    if (!meetingId || !meetingCode) {
      setError("Meeting ID không hợp lệ");
      setIsConnecting(false);
      return;
    }

    if (!user?.id || !user?.fullName) {
      setError("Thông tin người dùng không hợp lệ");
      setIsConnecting(false);
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      // Lấy thông tin cuộc họp
      const detailMeeting = await getMeetingById(meetingId);
      if (!detailMeeting.data) {
        throw new Error("Không thể lấy thông tin cuộc họp");
      }

      const { participants } = detailMeeting.data;

      // Kiểm tra user có thuộc trong danh sách participants không
      const participant = participants.find(
        (p: MeetingParticipant) => p.userId === user.id
      );

      if (!participant) {
        throw new Error("Bạn không có quyền tham gia cuộc họp");
      }

      const role = participant.role ?? "";

      // Gọi API để lấy token từ backend
      const response = await getLivekitTokenApi({
        meetingCode: meetingCode,
        participantIdentity: user.id,
        participantName: user.fullName,
        role,
      });

      const { token, serverUrl: responseServerUrl } = response.data;

      if (!token) {
        throw new Error("Không thể lấy token kết nối");
      }

      const finalServerUrl = responseServerUrl || "wss://live.classvn.vn";

      // Kết nối vào LiveKit room
      await room.connect(finalServerUrl, token);

      toast.success("Đã tham gia cuộc họp");
      setIsConnecting(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Không thể kết nối đến cuộc họp";
      setError(errorMessage);
      setIsConnecting(false);
      toast.error(errorMessage);
    }
  }, [meetingId, room, user?.id, user?.fullName]);

  // Kết nối khi component mount
  useEffect(() => {
    let isMounted = true;

    const initConnection = async () => {
      if (isMounted) {
        await fetchTokenAndConnect();
      }
    };

    initConnection();

    return () => {
      isMounted = false;
    };
  }, [fetchTokenAndConnect]);

  // Xử lý disconnect khi component unmount
  useEffect(() => {
    const handleDisconnect = () => {
      toast.info("Đã rời khỏi cuộc họp");
      navigate("/meetings");
    };

    room.on(RoomEvent.Disconnected, handleDisconnect);

    return () => {
      room.off(RoomEvent.Disconnected, handleDisconnect);
      if (room.state === "connected") {
        room.disconnect();
      }
    };
  }, [room, navigate]);

  const handleToggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);

  const handleToggleControlPanel = useCallback(() => {
    setIsControlPanelOpen((prev) => !prev);
  }, []);

  const handleLeave = useCallback(async () => {
    try {
      if (room.state === "connected") {
        await room.disconnect();
      }
      navigate("/meetings");
    } catch (error) {
      console.error("Error leaving meeting:", error);
      navigate("/meetings");
    }
  }, [room, navigate]);

  // UI Loading
  if (isConnecting) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-lg font-medium">Đang kết nối đến cuộc họp...</p>
        </div>
      </div>
    );
  }

  // UI Error
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <div className="mb-4 text-4xl">⚠️</div>
          <h2 className="mb-2 text-xl font-semibold text-destructive">
            Lỗi kết nối
          </h2>
          <p className="mb-4 text-muted-foreground">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/meetings")}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            aria-label="Quay lại danh sách cuộc họp"
          >
            Quay lại danh sách cuộc họp
          </button>
        </div>
      </div>
    );
  }

  // Hiển thị UI video conference
  return (
    <RoomContext.Provider value={room}>
      <div
        data-lk-theme="default"
        className="flex h-screen bg-[#0f0f0f] overflow-hidden"
      >
        {/* Main video area */}
        <div className="flex-1 flex flex-col relative">
          <div className="flex-1 relative bg-black">
            <MyVideoConference />
          </div>
          {/* Participant name overlay */}

          <BottomControlBar
            room={room}
            onToggleChat={handleToggleChat}
            onToggleControlPanel={handleToggleControlPanel}
            onLeave={handleLeave}
          />
        </div>

        {/* Right sidebar - Participants Grid or Chat */}
        {isChatOpen ? (
          <MeetingChatPanel
            room={room}
            currentUserName={user?.fullName || user?.id || "User"}
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
          />
        ) : (
          <ParticipantsGrid room={room} />
        )}

        {/* Control Panel Modal */}
        <ControlPanel
          room={room}
          isOpen={isControlPanelOpen}
          onClose={() => setIsControlPanelOpen(false)}
        />

        <RoomAudioRenderer />
      </div>
    </RoomContext.Provider>
  );
}

function MyVideoConference() {
  // useTracks trả về tất cả camera và screen share tracks
  // Nếu user tham gia không có camera track, placeholder track sẽ được trả về
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Tách screen share và camera tracks
  const screenShareTracks = tracks.filter(
    (track) => track.source === Track.Source.ScreenShare
  );
  const cameraTracks = tracks.filter(
    (track) => track.source === Track.Source.Camera
  );

  // Nếu có screen share, hiển thị nó làm main view
  if (screenShareTracks.length > 0) {
    return (
      <div className="h-full w-full">
        <GridLayout tracks={screenShareTracks} style={{ height: "100%" }}>
          <ParticipantTile />
        </GridLayout>
      </div>
    );
  }

  // Nếu chỉ có camera tracks, hiển thị grid
  return (
    <div className="h-full w-full">
      <GridLayout tracks={cameraTracks} style={{ height: "100%" }}>
        <ParticipantTile />
      </GridLayout>
    </div>
  );
}
